import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FacilitiesMaintenance from './FacilitiesMaintenance';
import FacilitiesAssets from './FacilitiesAssets';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// The Facilities hub — one top-level page with an internal tab bar, mirroring the
// Human Resources hub. Access is gated to Michael, Jon, and Steven in menuModel
// (gate: 'facilities'); this component is a defensive no-op for anyone else.
// Overview is a roll-up; Rentals / Forms are scaffolds ready to be
// wired to real data.
export default function Facilities({ currentUser }) {
  const [tab, setTab] = useState('overview');

  const tabs = [
    { key: 'overview',    label: 'Overview' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'rentals',     label: 'Rentals' },
    { key: 'forms',       label: 'Forms' },
  ];

  const renderTab = () => {
    switch (tab) {
      case 'overview':    return <Overview onNavigate={setTab} />;
      case 'maintenance': return <FacilitiesMaintenance />;
      case 'rentals':     return <Rentals />;
      case 'forms':       return <Forms />;
      default:            return null;
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '2px', padding: '0 28px', borderBottom: '1px solid #EDEDF0', background: '#fff', overflowX: 'auto' }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: '15px 16px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '14px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#8A8A8E',
                borderBottom: active ? `2.5px solid ${ORANGE}` : '2.5px solid transparent',
                whiteSpace: 'nowrap', marginBottom: '-1px', transition: 'color .15s',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '24px 28px' }}>{renderTab()}</div>
    </div>
  );
}

// ---- shared building blocks (match HR Overview / site palette) ----
const card = { background: '#fff', border: '1px solid #EDEDF0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const label = { fontSize: '11.5px', fontWeight: 600, color: '#8A8A8E', textTransform: 'uppercase', letterSpacing: '0.05em' };

function SectionHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px', gap: '16px', flexWrap: 'wrap' }}>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function PrimaryBtn({ children, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 18px', background: ORANGE, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
      {children}
    </button>
  );
}

function EmptyPanel({ icon, title, note }) {
  return (
    <div style={{ ...card, padding: '56px 24px', textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: '14px', background: '#F4F6F9', color: NAVY, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>{icon}</div>
      <div style={{ fontSize: '15px', fontWeight: 700, color: NAVY }}>{title}</div>
      <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '6px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>{note}</div>
    </div>
  );
}

// ---- Overview ----
function Overview({ onNavigate }) {
  const [maint, setMaint] = useState(null); // { total, overdue }
  useEffect(() => {
    axios.get(API + '/facilities-maintenance', { headers: headers() })
      .then(r => {
        const t = r.data || [];
        const overdue = t.filter(x => { const nd = x.next_due ? String(x.next_due).slice(0, 10) : ''; return nd && nd < todayISO() && x.status !== 'Completed'; }).length;
        setMaint({ total: t.length, overdue });
      })
      .catch(() => setMaint({ total: 0, overdue: 0 }));
  }, []);
  const cards = [
    { key: 'maintenance', title: 'Maintenance', big: maint ? String(maint.total) : '—', sub: maint && maint.overdue ? `${maint.overdue} overdue` : 'recurring tasks', accent: !!(maint && maint.overdue) },
    { key: 'rentals',   title: 'Rentals',    big: '0', sub: 'upcoming bookings', accent: false },
    { key: 'forms',     title: 'Forms',      big: '5', sub: 'available',          accent: false },
  ];
  const hover = e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.09)'; };
  const out = e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; };
  return (
    <div>
      <SectionHeader title="Facilities" subtitle="Manage museum spaces, equipment, rentals, and facilities forms." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
        {cards.map(c => (
          <div key={c.key} style={{ ...card, padding: '20px 22px', cursor: c.noNav ? 'default' : 'pointer', transition: 'transform .15s, box-shadow .15s' }}
            onClick={() => !c.noNav && onNavigate(c.key)}
            onMouseEnter={c.noNav ? undefined : hover} onMouseLeave={c.noNav ? undefined : out}>
            <div style={{ ...label, marginBottom: '10px' }}>{c.title}</div>
            <div style={{ fontSize: '34px', fontWeight: 700, lineHeight: 1, color: c.accent ? ORANGE : NAVY }}>{c.big}</div>
            <div style={{ fontSize: '12.5px', color: '#6B7280', marginTop: '9px' }}>{c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Rentals (scaffold) ----
function Rentals() {
  return (
    <div>
      <SectionHeader title="Facility Rentals" subtitle="Manage bookings for museum spaces and events."
        action={<PrimaryBtn><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg>New Rental</PrimaryBtn>} />
      <EmptyPanel
        icon={<svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
        title="No rentals scheduled"
        note="Booked spaces, renters, dates, and status will appear here. Create a rental to get started." />
    </div>
  );
}

// ---- Forms (Software/Hardware FAQ is live; the rest are scaffolds) ----
function Forms() {
  const [open, setOpen] = useState(null); // 'assets' | null

  if (open === 'assets') {
    return (
      <div>
        <button onClick={() => setOpen(null)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#fff', border: '1px solid #e6e9ef', borderRadius: 8, color: NAVY, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: '8px 14px 8px 11px', marginBottom: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#F4F6F9'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          Back to Forms
        </button>
        <FacilitiesAssets />
      </div>
    );
  }

  const forms = [
    { name: 'Software / Hardware FAQ', desc: 'Full asset knowledge base — purchase, warranty, cost, license & support.', live: true, onClick: () => setOpen('assets') },
    { name: 'Maintenance Request',     desc: 'Report a repair or maintenance issue.' },
    { name: 'Rental Agreement',        desc: 'Space rental contract for events.' },
    { name: 'Incident Report',         desc: 'Document a facilities incident or hazard.' },
    { name: 'Key / Access Request',    desc: 'Request keys or door-access changes.' },
  ];
  return (
    <div>
      <SectionHeader title="Facilities Forms" subtitle="Request forms and documents for facilities operations." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
        {forms.map(f => (
          <div key={f.name} onClick={f.onClick}
            style={{ ...card, padding: '18px 20px', display: 'flex', gap: '14px', alignItems: 'flex-start', cursor: 'pointer', transition: 'transform .15s, box-shadow .15s', position: 'relative' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.09)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}>
            <div style={{ width: 40, height: 40, borderRadius: '10px', background: '#FFF1E9', color: ORANGE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: NAVY }}>{f.name}
                {f.live && <span style={{ marginLeft: 8, fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#047857', background: '#ECFDF5', borderRadius: 5, padding: '2px 7px', verticalAlign: 'middle' }}>Live</span>}
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '3px' }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '16px' }}>The Software / Hardware FAQ is live. Remaining forms are placeholders — link each to its live PDF or online form once available.</p>
    </div>
  );
}
